/**
 * board-lanes — the pure status→lane grouping and per-lane card differentiator
 * logic for the Run Board (Story D). No DOM, no React: every rule here is a pure
 * function so the lane mapping, counts, and card text are unit-testable in
 * isolation, and the React layer only renders what these return.
 *
 * The design spec's contract ("The 8-status machine, grouped into 5 lanes" +ﾠ
 * "Status → lane mapping, without dot colors"):
 *
 *   - The COLUMN is the status — there is no per-card status dot. Within a lane
 *     every card shares one status, so what differentiates cards is a TEXT token
 *     specific to that lane, never a colored indicator.
 *   - Red is the only learned color and means exactly one thing: a real failure
 *     (`failed`). Everything else is monochrome text weighted by typography.
 */

import type { TrialLite, TrialStatus } from './board-types'
import type { RunStatusCounts } from './types'

/** The five board lanes, in render order. */
export type LaneId = 'queued' | 'leased' | 'running' | 'recovering' | 'done'

/** Lane metadata for the header strip. */
export interface LaneMeta {
  id: LaneId
  /** Column header label (uppercased in the spec sketch). */
  label: string
}

/** The lanes in their fixed left-to-right order. */
export const LANES: readonly LaneMeta[] = [
  { id: 'queued', label: 'QUEUED' },
  { id: 'leased', label: 'LEASED' },
  { id: 'running', label: 'RUNNING' },
  { id: 'recovering', label: 'RECOVERING' },
  { id: 'done', label: 'DONE' },
]

/**
 * Map one of the eight store statuses to its board lane.
 *
 *   queued, retryable      → Queued      (a retryable job is re-queued)
 *   leased                 → Leased
 *   running                → Running
 *   interrupted            → Recovering  (mid-reconcile)
 *   succeeded/failed/cancelled → Done
 *
 * This mirrors the read model's own lane grouping (`isQueuedLane` folds
 * `retryable` in with `queued`), so the board's per-lane counts match the
 * snapshot counts exactly.
 */
export function laneForStatus(status: TrialStatus): LaneId {
  switch (status) {
    case 'queued':
    case 'retryable':
      return 'queued'
    case 'leased':
      return 'leased'
    case 'running':
      return 'running'
    case 'interrupted':
      return 'recovering'
    case 'succeeded':
    case 'failed':
    case 'cancelled':
      return 'done'
  }
}

/**
 * Lane for a whole trial, honoring its rate-limit `recovering` flag (EN-3): a
 * waiting trial whose provider is cooling renders in Recovering rather than
 * Queued. Every other trial falls through to {@link laneForStatus}. Prefer this
 * over `laneForStatus(trial.status)` anywhere a full {@link TrialLite} is in
 * hand, so a backing-off trial lands in the lane the operator expects.
 */
export function laneForTrial(trial: TrialLite): LaneId {
  if (trial.recovering) {
    return 'recovering'
  }
  return laneForStatus(trial.status)
}

/**
 * Per-lane counts derived from the snapshot's eight-status `counts`, so the
 * column headers read the authoritative store totals — not the length of the
 * (paginated) `trials` array.
 */
export function laneCounts(counts: RunStatusCounts, recoveringCount = 0): Record<LaneId, number> {
  // `recoveringCount` are waiting trials whose provider is rate-limit cooling
  // (EN-3). They are counted in `counts.queued`/`counts.retryable` by the store,
  // so move them from the Queued lane into Recovering to match where their cards
  // render. Clamp so a stale snapshot can never drive Queued negative.
  const cooling = Math.min(recoveringCount, counts.queued + counts.retryable)
  return {
    queued: counts.queued + counts.retryable - cooling,
    leased: counts.leased,
    running: counts.running,
    recovering: counts.interrupted + cooling,
    done: counts.succeeded + counts.failed + counts.cancelled,
  }
}

/** Group the loaded trials into their lanes, preserving snapshot order. */
export function groupByLane(trials: readonly TrialLite[]): Record<LaneId, TrialLite[]> {
  const lanes: Record<LaneId, TrialLite[]> = {
    queued: [],
    leased: [],
    running: [],
    recovering: [],
    done: [],
  }
  for (const trial of trials) {
    lanes[laneForTrial(trial)].push(trial)
  }
  return lanes
}

/**
 * The card's per-lane text differentiator.
 *
 * `text` is the lane-specific token (wait time, sub-phase+dwell, retry counter,
 * pass/fail glyph). `isFailure` is true ONLY for a `failed` trial — it is the
 * single signal the renderer paints red; nothing else carries the failure color.
 */
export interface CardDifferentiator {
  text: string
  isFailure: boolean
}

/**
 * Compute the differentiator for a card, given the snapshot timestamp as `now`
 * (dwell/wait are measured against the snapshot, not the wall clock, so the text
 * is consistent with the rest of the rendered board).
 */
export function cardDifferentiator(trial: TrialLite, now: number): CardDifferentiator {
  switch (laneForTrial(trial)) {
    case 'queued':
      return { text: queuedText(trial, now), isFailure: false }
    case 'leased':
      // Leased is a transient stage: the arm tag (rendered separately) is enough,
      // so there is no extra differentiator token.
      return { text: '', isFailure: false }
    case 'running':
      return { text: runningText(trial), isFailure: false }
    case 'recovering':
      return { text: recoveringText(trial), isFailure: false }
    case 'done':
      return doneText(trial)
  }
}

/**
 * Recovering differentiator. A rate-limit `recovering` trial (provider cooling)
 * reads `⟳ rate-limited`; a mid-reconcile `interrupted` trial keeps the retry
 * counter `⟳ retry a/b`. Both share the lane but tell the operator a different
 * story about why the trial is parked.
 */
function recoveringText(trial: TrialLite): string {
  if (trial.recovering && trial.status !== 'interrupted') {
    return '⟳ rate-limited'
  }
  return `⟳ retry ${trial.attempt}/${trial.maxAttempts}`
}

/** Queued: how long the trial has been waiting, plus a small retry tag if re-queued. */
function queuedText(trial: TrialLite, now: number): string {
  const wait = `wait ${formatDurationShort(Math.max(0, now - trial.createdAt))}`
  return trial.status === 'retryable' ? `${wait} · retry` : wait
}

/**
 * Running: the live sub-phase and its dwell (`vmboot 6m04s`) when a phase is
 * retained. `latestPhase`/`phaseDwellMs` are null until Story E wires the
 * latest-phase map, so the card shows a blank differentiator until then — not a
 * placeholder, by design.
 */
function runningText(trial: TrialLite): string {
  if (trial.latestPhase === null) {
    return ''
  }
  if (trial.phaseDwellMs === null) {
    return trial.latestPhase
  }
  return `${trial.latestPhase} ${formatDwell(trial.phaseDwellMs)}`
}

/** Done: pass/fail/cancel glyph; only `failed` is a failure (painted red). */
function doneText(trial: TrialLite): CardDifferentiator {
  if (trial.status === 'succeeded') {
    return { text: '✓', isFailure: false }
  }
  if (trial.status === 'cancelled') {
    return { text: '⊘', isFailure: false }
  }
  // failed: decorate with the error class so an engineer reads the blame at a glance.
  const cls = trial.errorClass === 'scenario' ? 'scenario' : trial.errorClass === 'runner' ? 'runner' : ''
  return { text: cls ? `✗${cls}` : '✗', isFailure: true }
}

/** Done lane pass/fail split (`✓131 ✗11`) from the snapshot counts. */
export function donePassFail(counts: RunStatusCounts): { pass: number; fail: number } {
  return { pass: counts.succeeded, fail: counts.failed }
}

/**
 * Compact wait/dwell formatter: `44s`, `6m`, `1h 3m`. Distinct from the Running
 * sub-phase dwell (`formatDwell`), which keeps seconds for finer granularity.
 */
export function formatDurationShort(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000))
  if (totalSec < 60) {
    return `${totalSec}s`
  }
  const totalMin = Math.floor(totalSec / 60)
  if (totalMin < 60) {
    return `${totalMin}m`
  }
  const hours = Math.floor(totalMin / 60)
  const minutes = totalMin % 60
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

/** Running sub-phase dwell formatter, keeping seconds: `44s`, `6m04s`, `1h03m`. */
export function formatDwell(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000))
  if (totalSec < 60) {
    return `${totalSec}s`
  }
  const totalMin = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  if (totalMin < 60) {
    return `${totalMin}m${String(sec).padStart(2, '0')}s`
  }
  const hours = Math.floor(totalMin / 60)
  const min = totalMin % 60
  return `${hours}h${String(min).padStart(2, '0')}m`
}
